import * as React from 'react';
import {
  Archive,
  Bot,
  ChevronDown,
  ChevronUp,
  Loader2,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input, Textarea } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Slider } from '../../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../components/ui/toast';
import {
  LLM_CATALOG,
  findModel,
  type AgentPresetUpsertInput,
  type FillerCategoryConfig,
  type LlmProvider,
} from '@cp3/shared';
import { HeaderActions } from '../../components/HeaderActions';
import { cn } from '../../lib/utils';
import {
  useAgentPresets,
  useDeleteAgentPreset,
  useReorderAgentPresets,
  useSeedAgentPresets,
  useUpsertAgentPreset,
  type AgentPresetRow,
} from '../../hooks/useAgentPresets';
import { useElevenLabsVoices } from '../../hooks/useElevenLabsVoices';

const DEFAULT_FILLER_CATEGORIES: FillerCategoryConfig[] = [
  { category: 'mhm', enabled: true, minDelayMs: 600, maxPerCall: 3 },
  { category: 'wait-phrase', enabled: true, minDelayMs: 1500, maxPerCall: 2 },
  { category: 'breath', enabled: true, minDelayMs: 800, maxPerCall: 4 },
  { category: 'filler-word', enabled: true, minDelayMs: 700, maxPerCall: 3 },
  { category: 'throat-clear', enabled: false, minDelayMs: 0, maxPerCall: 1 },
  { category: 'cough', enabled: false, minDelayMs: 0, maxPerCall: 1 },
];

const FILLER_CATEGORY_LABEL: Record<FillerCategoryConfig['category'], string> = {
  'breath': 'Atemzug',
  'mhm': 'Mhm / Aha',
  'filler-word': 'Füllwort',
  'wait-phrase': 'Wartephrase',
  'throat-clear': 'Räuspern',
  'cough': 'Husten',
};

function emptyPreset(): AgentPresetUpsertInput {
  return {
    id: '',
    name: '',
    description: '',
    icon: '',
    sortOrder: 100,
    archived: false,
    llmProvider: 'openai',
    llmModel: 'gpt-4.1-mini',
    llmTemperature: 0.3,
    llmMaxTokens: 200,
    systemPrompt:
      'Du bist ein freundlicher, kompetenter Telefon-Assistent. Halte dich kurz, ' +
      'frage nach Name und Anliegen, schlage passende Termine aus den verfügbaren ' +
      'Zeitfenstern vor und bestätige Buchungen klar.',
    sttProvider: 'deepgram',
    ttsProvider: 'elevenlabs',
    ttsVoiceId: '21m00Tcm4TlvDq8ikWAM',
    ttsModel: 'eleven_flash_v2_5',
    ttsStability: 0.5,
    ttsSimilarityBoost: 0.75,
    fillerEnabled: true,
    fillerThresholdMs: 800,
    fillerStrategy: 'context-aware',
    fillerCategories: DEFAULT_FILLER_CATEGORIES,
    maxFillerPerCall: 5,
    fillerAlwaysPlay: false,
    voiceAgentMode: 'classic',
    nativeVoiceAgent: null,
    language: 'de-DE',
    promptCallerIdSecurity: null,
    promptConversationStyle: null,
    promptAppointmentWorkflow: null,
    promptTicketCreation: null,
    promptTicketLookup: null,
  };
}

export function AgentPresetsPage() {
  const presets = useAgentPresets();
  const reorder = useReorderAgentPresets();
  const seed = useSeedAgentPresets();
  const { toast } = useToast();
  const [editing, setEditing] = React.useState<AgentPresetRow | 'new' | null>(null);

  async function runSeed(overwrite: boolean) {
    try {
      const r = await seed.mutateAsync(overwrite);
      toast({
        title: 'Standard-Agenten gesetzt',
        description: `${r.written.length} angelegt/aktualisiert, ${r.skipped.length} bereits vorhanden.`,
      });
    } catch (err) {
      toast({
        title: 'Seeding fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  async function moveRow(fromIdx: number, toIdx: number) {
    const list = presets.data ?? [];
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    if (fromIdx >= list.length || toIdx >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(fromIdx, 1);
    if (!moved) return;
    next.splice(toIdx, 0, moved);
    const entries = next.map((p, i) => ({ id: p.id, sortOrder: (i + 1) * 10 }));
    try {
      await reorder.mutateAsync(entries);
    } catch (err) {
      toast({
        title: 'Sortieren fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="space-y-6">
      <HeaderActions>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void runSeed(false)}
          disabled={seed.isPending}
        >
          {seed.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="mr-2 h-4 w-4" />
          )}
          Standard-Agenten anlegen
        </Button>
        <Button size="sm" onClick={() => setEditing('new')}>
          <Plus className="mr-2 h-4 w-4" /> Neuer Agent
        </Button>
      </HeaderActions>

      <Card>
        <CardHeader>
          <CardTitle>Agent-Presets</CardTitle>
          <CardDescription>
            Vordefinierte Sprach-Agenten für Pro-Nutzer. Pro-User wählen einen
            Eintrag aus dieser Liste — KI-Modell, Stimme, Sprache, Latenz und
            Verhalten kommen dann aus dem Preset. Begrüßung, Verabschiedung
            und Wissensbasis bleiben pro Nutzer konfigurierbar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {presets.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !presets.data || presets.data.length === 0 ? (
            <EmptyState onCreate={() => setEditing('new')} />
          ) : (
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Reihenfolge</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Modell</TableHead>
                    <TableHead>Stimme</TableHead>
                    <TableHead>Sprache</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {presets.data.map((p, idx) => (
                    <PresetRow
                      key={p.id}
                      preset={p}
                      onEdit={() => setEditing(p)}
                      onMoveUp={idx === 0 ? null : () => void moveRow(idx, idx - 1)}
                      onMoveDown={
                        idx === (presets.data?.length ?? 0) - 1
                          ? null
                          : () => void moveRow(idx, idx + 1)
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {presets.data && presets.data.length > 0 && (
            <div className="mt-3 space-y-3 md:hidden">
              {presets.data.map((p) => (
                <PresetCard key={p.id} preset={p} onEdit={() => setEditing(p)} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <EditPresetDialog
          preset={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function PresetRow({
  preset,
  onEdit,
  onMoveUp,
  onMoveDown,
}: {
  preset: AgentPresetRow;
  onEdit: () => void;
  onMoveUp: (() => void) | null;
  onMoveDown: (() => void) | null;
}) {
  const remove = useDeleteAgentPreset();
  const { toast } = useToast();

  async function archive() {
    if (preset.archived) return;
    if (!confirm(`Agent „${preset.name}" wirklich archivieren?`)) return;
    try {
      await remove.mutateAsync(preset.id);
      toast({ title: 'Agent archiviert' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const modelLabel =
    findModel(preset.llmProvider, preset.llmModel)?.label ?? preset.llmModel;

  return (
    <TableRow className={cn(preset.archived && 'opacity-60')}>
      <TableCell>
        <div className="flex flex-col items-center gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={() => onMoveUp?.()}
            disabled={!onMoveUp}
            aria-label="Höher sortieren"
          >
            <ChevronUp className="size-3" />
          </Button>
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {preset.sortOrder ?? 0}
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={() => onMoveDown?.()}
            disabled={!onMoveDown}
            aria-label="Tiefer sortieren"
          >
            <ChevronDown className="size-3" />
          </Button>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="text-lg">{preset.icon || '🤖'}</span>
          <div className="flex flex-col">
            <span className="font-medium">{preset.name}</span>
            <span className="font-mono text-xs text-muted-foreground">{preset.id}</span>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span className="text-sm">{modelLabel}</span>
          <span className="text-xs text-muted-foreground">
            {LLM_CATALOG[preset.llmProvider]?.label ?? preset.llmProvider}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <span className="font-mono text-xs">{preset.ttsVoiceId.slice(0, 12)}…</span>
      </TableCell>
      <TableCell>
        <Badge variant="muted">{preset.language}</Badge>
      </TableCell>
      <TableCell>
        {preset.archived ? (
          <Badge variant="muted">Archiviert</Badge>
        ) : (
          <Badge variant="success">Aktiv</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={archive}
            disabled={preset.archived || remove.isPending}
            title="Archivieren"
          >
            <Archive className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function PresetCard({ preset, onEdit }: { preset: AgentPresetRow; onEdit: () => void }) {
  return (
    <Card className={cn(preset.archived && 'opacity-60')}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-lg">{preset.icon || '🤖'}</span>
              <span className="truncate font-semibold">{preset.name}</span>
            </div>
            <div className="font-mono text-xs text-muted-foreground">{preset.id}</div>
          </div>
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="muted">{preset.language}</Badge>
          <Badge variant={preset.archived ? 'muted' : 'success'}>
            {preset.archived ? 'Archiviert' : 'Aktiv'}
          </Badge>
        </div>
        {preset.description && (
          <p className="text-xs text-muted-foreground">{preset.description}</p>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border p-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
        <Bot className="h-5 w-5 text-primary" />
      </div>
      <div className="text-sm font-medium">Noch keine Agent-Presets</div>
      <p className="max-w-sm text-xs text-muted-foreground">
        Lege deinen ersten Agent an. Pro-User wählen ihn dann mit einem Klick aus
        und du behältst die Kontrolle über Modell, Stimme und Verhalten.
      </p>
      <Button onClick={onCreate}>
        <Plus className="h-4 w-4" /> Ersten Agent anlegen
      </Button>
    </div>
  );
}

function EditPresetDialog({
  preset,
  onClose,
}: {
  preset: AgentPresetRow | null;
  onClose: () => void;
}) {
  const upsert = useUpsertAgentPreset();
  const { toast } = useToast();
  const isEdit = preset != null;
  const [form, setForm] = React.useState<AgentPresetUpsertInput>(() =>
    preset ? toFormState(preset) : emptyPreset(),
  );

  const providerEntry = LLM_CATALOG[form.llmProvider];
  const voicesQuery = useElevenLabsVoices({ source: 'all', language: 'de' });
  const voiceOptions = voicesQuery.data ?? [];

  function update<K extends keyof AgentPresetUpsertInput>(
    key: K,
    value: AgentPresetUpsertInput[K],
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateFillerCategory(
    idx: number,
    patch: Partial<FillerCategoryConfig>,
  ) {
    setForm((prev) => ({
      ...prev,
      fillerCategories: prev.fillerCategories.map((c, i) =>
        i === idx ? { ...c, ...patch } : c,
      ),
    }));
  }

  // Active wizard tab. Free navigation — der User darf jederzeit
  // zwischen den Schritten wechseln, kein erzwungener Forward-Flow.
  const [tab, setTab] = React.useState<'basis' | 'model' | 'voice' | 'filler'>(
    'basis',
  );

  async function onSave() {
    if (!form.name.trim()) {
      toast({ title: 'Name fehlt', variant: 'destructive' });
      return;
    }
    const finalId = form.id.trim() || autoSlug(form.name);
    if (!finalId) {
      toast({ title: 'ID fehlt', variant: 'destructive' });
      return;
    }
    try {
      await upsert.mutateAsync({ ...form, id: finalId });
      toast({ title: isEdit ? 'Agent aktualisiert' : 'Agent angelegt' });
      onClose();
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Agent bearbeiten' : 'Neuer Agent'}</DialogTitle>
          <DialogDescription>
            Lege Modell, Stimme, Sprache und Verhalten fest. Pro-Nutzer sehen
            anschließend nur Name + Beschreibung in der Auswahl.
          </DialogDescription>
        </DialogHeader>

        {/* Wizard-Tabs — frei navigierbar, jeder Schritt überspringbar.
            Erstellung und Bearbeitung teilen das gleiche Fenster: bei
            "Neuer Agent" startet `form` aus emptyPreset(), bei "Bearbeiten"
            aus dem geladenen AgentPreset. Speichern-Button bleibt unten
            sichtbar; jeder Tab kann auch ohne die anderen abgeschickt
            werden, weil der Server-Schema-Validator pro Feld defaultet. */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basis">1 · Basis</TabsTrigger>
            <TabsTrigger value="model">2 · KI-Modell</TabsTrigger>
            <TabsTrigger value="voice">3 · Stimme</TabsTrigger>
            <TabsTrigger value="filler">4 · Filler</TabsTrigger>
          </TabsList>

          {/* ── Schritt 1 — Basisdaten ──────────────────────────── */}
          <TabsContent value="basis" className="space-y-4 pt-2">
            <p className="text-xs text-muted-foreground">
              Pflichtangaben für die Auswahl-Seite. Nur Name + Beschreibung
              sehen Pro-User später; ID und Sortierung sind admin-intern.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="agent-name">Name</Label>
                <Input
                  id="agent-name"
                  value={form.name}
                  onChange={(e) => {
                    update('name', e.target.value);
                    if (!isEdit && !form.id) update('id', autoSlug(e.target.value));
                  }}
                  placeholder="z.B. Standard Termin-Agent"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="agent-id">ID</Label>
                <Input
                  id="agent-id"
                  value={form.id}
                  disabled={isEdit}
                  onChange={(e) => update('id', e.target.value)}
                  placeholder="standard-termin"
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-[100px_1fr]">
              <div className="space-y-1.5">
                <Label htmlFor="agent-icon">Icon</Label>
                <Input
                  id="agent-icon"
                  value={form.icon ?? ''}
                  onChange={(e) => update('icon', e.target.value)}
                  placeholder="🤖"
                  maxLength={4}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="agent-desc">Beschreibung</Label>
                <Input
                  id="agent-desc"
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  placeholder="Kurze Erklärung für die Auswahl-Seite"
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="agent-sort">Sortierung</Label>
                <Input
                  id="agent-sort"
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) =>
                    update('sortOrder', parseInt(e.target.value || '0', 10) || 0)
                  }
                />
              </div>
              <label className="flex items-end gap-2 pb-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.archived}
                  onChange={(e) => update('archived', e.target.checked)}
                />
                Archiviert (in der Auswahl ausgeblendet)
              </label>
            </div>
            <div className="flex justify-end pt-2">
              <Button size="sm" variant="outline" onClick={() => setTab('model')}>
                Weiter zu KI-Modell →
              </Button>
            </div>
          </TabsContent>

          {/* ── Schritt 2 — KI-Modell + Verhaltensregeln ────────── */}
          <TabsContent value="model" className="space-y-4 pt-2">
            <p className="text-xs text-muted-foreground">
              Provider, Modell und Sampling-Parameter. Untenstehend zusätzlich
              die fünf Verhaltens-Override-Blöcke, die das voice-agent-Backend
              an den System-Prompt anhängt.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Provider</Label>
                <Select
                  value={form.llmProvider}
                  onValueChange={(v) => {
                    const p = v as LlmProvider;
                    const first = LLM_CATALOG[p].models[0]?.id ?? '';
                    setForm((prev) => ({ ...prev, llmProvider: p, llmModel: first }));
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.values(LLM_CATALOG).map((entry) => (
                      <SelectItem key={entry.provider} value={entry.provider}>
                        {entry.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Modell</Label>
                {providerEntry.freeForm ? (
                  <Input
                    value={form.llmModel}
                    onChange={(e) => update('llmModel', e.target.value)}
                  />
                ) : (
                  <Select
                    value={form.llmModel}
                    onValueChange={(v) => update('llmModel', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {providerEntry.models.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <SliderField
                label={`Temperature: ${form.llmTemperature.toFixed(2)}`}
                value={form.llmTemperature}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => update('llmTemperature', v)}
              />
              <SliderField
                label={`Max Tokens: ${form.llmMaxTokens}`}
                value={form.llmMaxTokens}
                min={50}
                max={2000}
                step={10}
                onChange={(v) => update('llmMaxTokens', Math.round(v))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="agent-prompt">System-Prompt</Label>
              <Textarea
                id="agent-prompt"
                rows={8}
                value={form.systemPrompt}
                onChange={(e) => update('systemPrompt', e.target.value)}
              />
              <div className="text-xs text-muted-foreground">
                {form.systemPrompt.length} / 8.000 Zeichen
              </div>
            </div>

            <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Verhaltens-Overrides (optional)
              </div>
              <p className="text-xs text-muted-foreground">
                Leer lassen → Backend nutzt die im voice-agent hinterlegten
                Default-Blöcke. Setze einen Text, um den jeweiligen Block
                pro Preset zu überschreiben.
              </p>
              {(
                [
                  ['promptCallerIdSecurity', 'Anrufer-Identität & GDPR'],
                  ['promptConversationStyle', 'Konversations-Stil'],
                  ['promptAppointmentWorkflow', 'Termin-Workflow'],
                  ['promptTicketCreation', 'Ticket-Erstellung'],
                  ['promptTicketLookup', 'Ticket-Abfrage'],
                ] as Array<[keyof AgentPresetUpsertInput, string]>
              ).map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  <Textarea
                    rows={3}
                    value={(form[key] as string | null | undefined) ?? ''}
                    placeholder="Leer = Default des voice-agent"
                    className="font-mono text-xs"
                    onChange={(e) =>
                      update(
                        key,
                        (e.target.value || null) as AgentPresetUpsertInput[typeof key],
                      )
                    }
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-between pt-2">
              <Button size="sm" variant="ghost" onClick={() => setTab('basis')}>
                ← Basis
              </Button>
              <Button size="sm" variant="outline" onClick={() => setTab('voice')}>
                Weiter zu Stimme →
              </Button>
            </div>
          </TabsContent>

          {/* ── Schritt 3 — Stimme & Sprache ────────────────────── */}
          <TabsContent value="voice" className="space-y-4 pt-2">
            <p className="text-xs text-muted-foreground">
              STT-Provider, ElevenLabs-Stimme + TTS-Modell + Sprache. Stability
              und Similarity Boost steuern, wie streng die Stimme dem
              ElevenLabs-Sample folgt — höhere Werte = konsistenter, niedrigere
              = ausdrucksstärker.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>STT-Provider</Label>
                <Select
                  value={form.sttProvider}
                  onValueChange={(v) =>
                    update('sttProvider', v as 'deepgram' | 'elevenlabs')
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deepgram">Deepgram</SelectItem>
                    <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sprache</Label>
                <Select
                  value={form.language}
                  onValueChange={(v) =>
                    update('language', v as AgentPresetUpsertInput['language'])
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de-DE">Deutsch (DE)</SelectItem>
                    <SelectItem value="de-AT">Deutsch (AT)</SelectItem>
                    <SelectItem value="en-US">English (US)</SelectItem>
                    <SelectItem value="multi">Mehrsprachig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>ElevenLabs Voice</Label>
                {voiceOptions.length > 0 ? (
                  <Select
                    value={form.ttsVoiceId}
                    onValueChange={(v) => update('ttsVoiceId', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {voiceOptions.map((v) => (
                        <SelectItem key={v.voice_id} value={v.voice_id}>
                          {v.name} {v.language ? `· ${v.language}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={form.ttsVoiceId}
                    onChange={(e) => update('ttsVoiceId', e.target.value)}
                    placeholder="Voice ID"
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <Label>TTS-Modell</Label>
                <Select
                  value={form.ttsModel}
                  onValueChange={(v) =>
                    update('ttsModel', v as AgentPresetUpsertInput['ttsModel'])
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eleven_flash_v2_5">Flash v2.5 (schnell)</SelectItem>
                    <SelectItem value="eleven_multilingual_v2">
                      Multilingual v2 (Qualität)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <SliderField
                label={`Stability: ${form.ttsStability.toFixed(2)}`}
                value={form.ttsStability}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => update('ttsStability', v)}
              />
              <SliderField
                label={`Similarity Boost: ${form.ttsSimilarityBoost.toFixed(2)}`}
                value={form.ttsSimilarityBoost}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => update('ttsSimilarityBoost', v)}
              />
            </div>
            <div className="flex justify-between pt-2">
              <Button size="sm" variant="ghost" onClick={() => setTab('model')}>
                ← KI-Modell
              </Button>
              <Button size="sm" variant="outline" onClick={() => setTab('filler')}>
                Weiter zu Filler →
              </Button>
            </div>
          </TabsContent>

          {/* ── Schritt 4 — Latenz & Filler ─────────────────────── */}
          <TabsContent value="filler" className="space-y-4 pt-2">
            <p className="text-xs text-muted-foreground">
              Filler-Audio überbrückt Latenz, wenn der LLM denkt. Pro Kategorie
              kannst du fein abstimmen, ab wann nach dem User-Stop frühestens
              gespielt werden darf und wie oft pro Anruf maximal.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.fillerEnabled}
                  onChange={(e) => update('fillerEnabled', e.target.checked)}
                />
                Filler aktiv
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.fillerAlwaysPlay ?? false}
                  onChange={(e) => update('fillerAlwaysPlay', e.target.checked)}
                />
                Immer abspielen (auch bei schnellem LLM)
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <SliderField
                label={`Schwelle: ${form.fillerThresholdMs} ms`}
                value={form.fillerThresholdMs}
                min={300}
                max={2000}
                step={50}
                onChange={(v) => update('fillerThresholdMs', Math.round(v))}
              />
              <SliderField
                label={`Max pro Anruf: ${form.maxFillerPerCall}`}
                value={form.maxFillerPerCall}
                min={0}
                max={20}
                step={1}
                onChange={(v) => update('maxFillerPerCall', Math.round(v))}
              />
              <div className="space-y-1.5">
                <Label>Strategie</Label>
                <Select
                  value={form.fillerStrategy}
                  onValueChange={(v) =>
                    update(
                      'fillerStrategy',
                      v as AgentPresetUpsertInput['fillerStrategy'],
                    )
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Zufällig</SelectItem>
                    <SelectItem value="context-aware">Kontextbewusst</SelectItem>
                    <SelectItem value="sequential">Reihenfolge</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">
                Alle Filler-Kategorien — Aktivierung, Min-Verzögerung, Max pro Anruf
              </Label>
              <div className="space-y-2">
                {form.fillerCategories.map((cat, idx) => (
                  <div
                    key={cat.category}
                    className={cn(
                      'rounded-md border p-3 transition-colors',
                      cat.enabled ? 'border-primary/40 bg-primary/5' : 'border-border bg-card',
                    )}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input
                          type="checkbox"
                          checked={cat.enabled}
                          onChange={(e) =>
                            updateFillerCategory(idx, { enabled: e.target.checked })
                          }
                        />
                        {FILLER_CATEGORY_LABEL[cat.category]}
                      </label>
                      <code className="text-[10px] text-muted-foreground">
                        {cat.category}
                      </code>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <SliderField
                        label={`Min-Verzögerung: ${cat.minDelayMs} ms`}
                        value={cat.minDelayMs}
                        min={0}
                        max={3000}
                        step={50}
                        onChange={(v) =>
                          updateFillerCategory(idx, { minDelayMs: Math.round(v) })
                        }
                      />
                      <div className="space-y-1.5">
                        <Label className="text-xs">
                          Max pro Anruf: {cat.maxPerCall}
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          max={20}
                          value={cat.maxPerCall}
                          onChange={(e) =>
                            updateFillerCategory(idx, {
                              maxPerCall: Math.max(
                                0,
                                Math.min(20, parseInt(e.target.value || '0', 10) || 0),
                              ),
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <Button size="sm" variant="ghost" onClick={() => setTab('voice')}>
                ← Stimme
              </Button>
              <span className="text-xs text-muted-foreground">
                Speichern unten — schließt nach erfolgreicher Übernahme.
              </span>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4" /> Abbrechen
          </Button>
          <Button onClick={onSave} disabled={upsert.isPending}>
            {upsert.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SliderField({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(vs) => onChange(vs[0] ?? value)}
      />
    </div>
  );
}

function autoSlug(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function toFormState(row: AgentPresetRow): AgentPresetUpsertInput {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? '',
    icon: row.icon ?? '',
    sortOrder: row.sortOrder ?? 100,
    archived: row.archived ?? false,
    llmProvider: row.llmProvider,
    llmModel: row.llmModel,
    llmTemperature: row.llmTemperature,
    llmMaxTokens: row.llmMaxTokens,
    systemPrompt: row.systemPrompt,
    sttProvider: row.sttProvider,
    ttsProvider: row.ttsProvider,
    ttsVoiceId: row.ttsVoiceId,
    ttsModel: row.ttsModel,
    ttsStability: row.ttsStability,
    ttsSimilarityBoost: row.ttsSimilarityBoost,
    fillerEnabled: row.fillerEnabled,
    fillerThresholdMs: row.fillerThresholdMs,
    fillerStrategy: row.fillerStrategy,
    fillerCategories:
      row.fillerCategories?.length > 0
        ? row.fillerCategories
        : DEFAULT_FILLER_CATEGORIES,
    maxFillerPerCall: row.maxFillerPerCall,
    fillerAlwaysPlay: row.fillerAlwaysPlay ?? false,
    voiceAgentMode: row.voiceAgentMode ?? 'classic',
    nativeVoiceAgent: row.nativeVoiceAgent ?? null,
    language: row.language,
    promptCallerIdSecurity: row.promptCallerIdSecurity ?? null,
    promptConversationStyle: row.promptConversationStyle ?? null,
    promptAppointmentWorkflow: row.promptAppointmentWorkflow ?? null,
    promptTicketCreation: row.promptTicketCreation ?? null,
    promptTicketLookup: row.promptTicketLookup ?? null,
  };
}
